"use client";
import React, { useMemo, useState } from "react";
import Layout from "./Layout";
import Link from "components/common/Link";
import { BookingQRModal } from "components/common";
import PackageDetailModal from "components/home/PackageDetailModal";
import PackageCard from "components/home/Card/PackageCard";
import { map, compact } from "utils/lodash";
import { useT, useI18n } from "providers/I18nProvider";
import useRotatingTagline from "hooks/useRotatingTagline";
import { toPackageInfo, PackageInfo } from "services/prepaidPackage/formatter";

export const PACKAGE_LIST_PATH = "/goi-tiet-kiem";

/**
 * Section "Ưu đãi gói tiết kiệm" — gói trả trước nhiều buổi (prepaidPackage).
 * 1 hàng cuộn ngang, "Xem tất cả" → /goi-tiet-kiem. Click card mở modal chi tiết → CTA dẫn về app (QR).
 */
const SectionPackage: React.FC<{
  title?: string;
  description?: string;
  banner?: string; // giữ prop để HomeContainer không đổi, section này không dùng banner
  items?: any[];
}> = ({ title, description, items }) => {
  const t = useT();
  const { locale } = useI18n();
  // Câu giới thiệu tự đổi mỗi 2 phút (4 biến thể trong file dịch)
  const rotatingDesc = useRotatingTagline("home.packagePromotionDesc", description);
  const [selected, setSelected] = useState<PackageInfo | null>(null);
  const [qrOpen, setQrOpen] = useState(false);

  // Chuẩn hoá + ẩn gói đã hết hạn mua (BE có thể vẫn để status active)
  const packages = useMemo(
    () =>
      compact(map(items, (item) => toPackageInfo(item?.prepaidPackage))).filter(
        (p) => p && !p.expired,
      ) as PackageInfo[],
    [items],
  );

  if (!packages.length) return null;

  return (
    <>
      <Layout
        iconName="sale"
        title={locale === "en" ? t("home.packagePromotion") : title || t("home.packagePromotion")}
        description={rotatingDesc}
        className="bg-gradient-to-b from-[#eefbfb] to-white"
        extra={
          <Link
            href={PACKAGE_LIST_PATH}
            className="tap-area inline-flex min-h-6 items-center text-sm font-bold text-primary-ink hover:underline"
          >
            {t("home.viewAll")} →
          </Link>
        }
      >
        {/* Cuộn ngang 1 hàng (chị Naomi chốt 02/10) — pt-2 -mt-2 chừa chỗ cho hover nhô lên, khỏi cắt viền */}
        <div className="flex items-stretch gap-3 md:gap-4 px-4 md:px-6 overflow-x-scroll scrollbar-hide snap-x snap-mandatory scroll-px-4 md:snap-none [&>*]:snap-start pt-2 -mt-2 pb-3">
          {packages.map((pkg) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              onSelect={setSelected}
              onBuy={() => setQrOpen(true)}
              compact
              className="w-[168px] md:w-[280px] flex-shrink-0"
            />
          ))}

          {/* Card cuối dải: nhảy sang trang toàn bộ gói (giống Ưu đãi trải nghiệm) */}
          <Link
            href={PACKAGE_LIST_PATH}
            className="group w-[110px] md:w-[200px] flex-shrink-0 rounded-2xl border-2 border-dashed border-primary/40 bg-[#eefbfb] flex flex-col items-center justify-center gap-2 md:gap-3 text-center p-3 md:p-5 transition-all duration-300 hover:-translate-y-1 hover:bg-[#dff6f5] hover:border-primary"
          >
            <span className="w-9 h-9 md:w-14 md:h-14 rounded-full bg-white text-primary shadow flex items-center justify-center text-lg md:text-2xl font-bold transition-transform group-hover:translate-x-1">
              →
            </span>
            <span className="font-bold text-black01 leading-snug text-[12px] md:text-base">
              {t("home.seeMorePackages")}
            </span>
            <span className="hidden md:block text-xs text-gray-500">{t("home.seeMorePackagesHint")}</span>
          </Link>
        </div>
      </Layout>

      {/* Bước 1: chi tiết gói */}
      <PackageDetailModal
        open={!!selected}
        pkg={selected}
        onClose={() => setSelected(null)}
        onBuy={() => {
          setSelected(null);
          setQrOpen(true);
        }}
      />

      {/* Bước 2: mua gói -> popup QR tải app */}
      <BookingQRModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        variant="package"
      />
    </>
  );
};

export default SectionPackage;
